/**
 * Typed RPG Publisher API accessors + React Query hooks.
 * This is the single shared data layer for all feature modules.
 */
import { useMemo } from 'react'
import { useQuery, useQueries, keepPreviousData } from '@tanstack/react-query'
import { rpg, app, ApiError } from './client'
import { getActiveWorldConfig, type FacetOption } from '@/lib/worldConfig'
import {
  ENTITY_TYPES,
  ZWEIHANDER_ENTITY_TYPES,
  ENTITY_META,
} from '@gamecodex/entity-ref'
import type {
  Book,
  BookFull,
  CharacterSchema,
  EntityFormat,
  EntityListResponse,
  EntityRef,
  EntityType,
  GameSystem,
  Kit3D,
  Monster,
  Paginated,
  SignedModelUrl,
  Spell,
  ZweihanderEntityType,
} from './types'

// Re-export constants for backward compatibility
export { ENTITY_TYPES, ZWEIHANDER_ENTITY_TYPES, ENTITY_META }

/**
 * Whether a game-system code is served by the agnostic `system_entity` store
 * (`/system-entities`) rather than the typed-5e `/entities/<type>` endpoints.
 * Driven by the active world's `gameSystems[].usesSystemEntities` flag — so a 5e
 * world (Grymwyrd) always returns false and stays on the legacy path. Reads the
 * config lazily inside the function (never at module load).
 */
export function usesSystemEntities(systemId?: string): boolean {
  if (!systemId) return false
  return getActiveWorldConfig().gameSystems.some((s) => s.id === systemId && s.usesSystemEntities === true)
}


/**
 * The encyclopedia type-facet list for a given system: the ZW `system_entity`
 * types when that system `usesSystemEntities`, else the default typed-5e list.
 * FacetSidebar/EncyclopediaPage call this so a ZW world shows ancestries/
 * professions/talents instead of classes/magic-items. The 5e path is unchanged
 * (returns the same {@link ENTITY_TYPES} array).
 */
export function getEntityTypes(systemId?: string): EntityType[] {
  return usesSystemEntities(systemId) ? (ZWEIHANDER_ENTITY_TYPES as EntityType[]) : ENTITY_TYPES
}

export const qk = {
  gameSystems: ['game-systems'] as const,
  books: (q?: Record<string, unknown>) => ['books', q] as const,
  book: (id: string) => ['book', id] as const,
  bookFull: (id: string) => ['book', id, 'full'] as const,
  entities: (type: string, params: Record<string, unknown>) => ['entities', type, params] as const,
  entity: (type: string, id: string, format: EntityFormat) => ['entity', type, id, format] as const,
  characterSchema: (code: string, locale?: string) => ['character-schema', code, locale] as const,
  monsters: (q?: Record<string, unknown>) => ['monsters', q] as const,
  spells: (q?: Record<string, unknown>) => ['spells', q] as const,
}

// ── Game systems ──────────────────────────────────────────────────────────
export function useGameSystems() {
  return useQuery({ queryKey: qk.gameSystems, queryFn: () => rpg.get<GameSystem[]>('/game-systems') })
}

/**
 * The per-`game_system` character-sheet JSON Schema, from
 * `GET /game-systems/:code/entity-schemas/character?locale`. Consumed by the
 * character builder (agent 2). `code` is the game-system code (e.g.
 * `zweihander-reforged`); pass `locale` to get api's translation overlay. api may
 * wrap the schema as `{ schema }` / `{ jsonSchema }` or return it bare — we unwrap
 * all three (matches play's `pickSchema`). Disabled when `code` is empty.
 *
 * NOTE: the `/game-systems/:code/entity-schemas/character` route is a SHARED
 * CONTRACT api must ship (play consumes the same path); see crossAppDeps.
 */
export function useCharacterSchema(code: string | undefined, locale?: string) {
  return useQuery<CharacterSchema | null>({
    enabled: !!code,
    queryKey: qk.characterSchema(code ?? '', locale),
    queryFn: async () => {
      const raw = await rpg.get<unknown>(
        `/game-systems/${encodeURIComponent(code!)}/entity-schemas/character`,
        locale ? { locale } : {},
      )
      return pickSchema(raw)
    },
    staleTime: 10 * 60 * 1000,
  })
}

/** Unwrap api's character-schema envelope ({schema}|{jsonSchema}|bare). */
function pickSchema(raw: unknown): CharacterSchema | null {
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) return null
  const obj = raw as Record<string, unknown>
  if (obj.schema && typeof obj.schema === 'object' && !Array.isArray(obj.schema)) return obj.schema as CharacterSchema
  if (obj.jsonSchema && typeof obj.jsonSchema === 'object' && !Array.isArray(obj.jsonSchema))
    return obj.jsonSchema as CharacterSchema
  return obj as CharacterSchema
}

// ── Books ─────────────────────────────────────────────────────────────────
export type BookListParams = {
  page?: number
  /** api's paging knob. (`limit`, `is_public` and `status` are NOT accepted by
   *  api's `listBooksSchema` — `additionalProperties: false` — and were being
   *  silently dropped; publish-state gating is `useStoreBooks`, not a query.) */
  pageSize?: number
  /**
   * Scope to one `pub_setting` (a real DB UUID, not a slug) — api's `GET
   * /books` already supports this (`src/routes/book.routes.ts`), it just
   * wasn't exposed here yet. Omit to leave books unscoped (legacy/default
   * behavior, unchanged for worlds that don't set `WorldConfig.settingId`).
   */
  setting_id?: string
}

/**
 * Books hidden from the website's listings (store grid, home "Featured", etc.).
 * INTERIM measure: site book visibility should ultimately be driven by the
 * web_publication_books link model (see _docs/03-web-publication-and-book-links.md)
 * so editors control this via the API instead of in code. Matched by id (stable)
 * and title (durable across reseeds).
 */
export const HIDDEN_BOOK_IDS = new Set<string>([
  '4f8e5be7-e415-45ff-8e21-25c8d41f0cff', // To Insanity and Beyond
  'de2a71e8-9eca-4bbb-9f41-8e158b915047', // Standard Template
])
export const HIDDEN_BOOK_TITLES = new Set<string>(['To Insanity and Beyond', 'Standard Template'])

export function isHiddenBook(b: Pick<Book, 'id' | 'title'>): boolean {
  return HIDDEN_BOOK_IDS.has(b.id) || HIDDEN_BOOK_TITLES.has(b.title.trim())
}

/**
 * NOTE (book scoping / crossAppDeps): api's `GET /books` list querystring is
 * `{ q, page, pageSize, setting_id }` with `additionalProperties: false` (see
 * api `src/schemas/book.schemas.ts`) — there is NO `game_system` filter, so we
 * still CANNOT scope the store/catalog to the active game system here without
 * api rejecting the request. `setting_id` IS now wired through (see
 * `BookListParams.setting_id` above) — pass `getActiveWorldConfig().settingId`
 * (a real `pub_setting` UUID, opt-in, distinct from `WorldConfig.settings[].id`
 * which is a slug) to scope a listing to one world's own books. Omit it and
 * books stay UNSCOPED (unchanged default for any world that doesn't set
 * `settingId` — was the only option for everyone until 2026-07-12). Full
 * per-world *store/catalog* scoping still needs an upstream `game_system` (or
 * publisher/product-line) filter, OR a per-publisher catalog — reported
 * upstream, unrelated to the narrow `setting_id` scoping this enables.
 */
export function useBooks(params: BookListParams = {}, opts: { enabled?: boolean } = {}) {
  return useQuery({
    enabled: opts.enabled ?? true,
    queryKey: qk.books(params),
    queryFn: () => rpg.get<Paginated<Book>>('/books', params),
    placeholderData: keepPreviousData,
    // Filter site-hidden books out of every listing; keep `total` consistent
    // with what's actually shown.
    select: (res) => {
      const data = res.data.filter((b) => !isHiddenBook(b))
      return { ...res, data, total: Math.max(0, res.total - (res.data.length - data.length)) }
    },
  })
}
// ── Atlas SKU catalog (real price + ownership + the LISTING gate) ──────────

/** Matches Atlas's real `GET /store/catalog` item shape (atlas/src/server/routes/store-catalog.ts). */
export interface AtlasCatalogItem {
  skuId: string
  code: string
  name: string
  rpgBookId: string | null
  /** Non-null when the SKU has an ACTIVE digital deliverable — i.e. a master
   *  PDF is uploaded in Atlas (`digital_deliverables.source_r2_key` is NOT
   *  NULL by schema, so active ⇒ file present). */
  deliverableId: string | null
  physical: boolean
  priceCents: number
  currency: string
  owned: boolean
}

interface AtlasCatalogResponse {
  gameSystem: string | null
  authenticated: boolean
  count: number
  catalog: AtlasCatalogItem[]
}

/**
 * Real price + ownership for books, from Atlas's SKU catalog, keyed by
 * `rpgBookId`. Fetched UNSCOPED on purpose: Atlas's `game_system` filter is
 * set on the *project*, and the book projects (GrymWyrd Omnibus, Pixen
 * Intrigue, …) carry `game_system = null` — so the old `?game_system=<world>`
 * scoping returned an empty catalog for every RPG world (verified live
 * 2026-09-13; it was never a "no SKUs yet" situation). World scoping of the
 * *book list* is api's `setting_id`, applied in `useStoreBooks`; the join on
 * `rpgBookId` then keeps only that world's sellable books.
 */
export function useBookCatalog() {
  const query = useQuery({
    queryKey: ['book-catalog'],
    queryFn: () => app.get<AtlasCatalogResponse>('/commerce/catalog'),
    staleTime: 60 * 1000,
  })
  const byBookId = useMemo(() => {
    const map = new Map<string, AtlasCatalogItem>()
    for (const item of query.data?.catalog ?? []) if (item.rpgBookId) map.set(item.rpgBookId, item)
    return map
  }, [query.data])
  return { byBookId, isLoading: query.isLoading, isError: query.isError, error: query.error, refetch: query.refetch }
}

/**
 * The listing rule (owner decision, Jared 2026-09-13): a book is visible on a
 * world site ONLY when Atlas has it set up as an entitlement with an uploaded
 * PDF — i.e. an active digital deliverable whose `rpgBookId` is this book.
 * api's own `status`/`is_public` flags are NOT the signal (every book is
 * `draft` there, and api's `/books` ignores those filters anyway).
 */
export function isSellableCatalogItem(item: AtlasCatalogItem | undefined): item is AtlasCatalogItem {
  return !!item && !!item.rpgBookId && !!item.deliverableId
}

export function sellableBooks(books: readonly Book[], catalog: ReadonlyMap<string, AtlasCatalogItem>): Book[] {
  return books.filter((b) => !isHiddenBook(b) && isSellableCatalogItem(catalog.get(b.id)))
}

/** The active world's shelf scope — its own `pub_setting` plus any extra book ids it opts into. */
export function worldShelfScope(): StoreBooksScope {
  const { settingId, extraBookIds } = getActiveWorldConfig()
  return { setting_id: settingId, extra_book_ids: extraBookIds }
}

export type StoreBooksScope = {
  /** The world's own `pub_setting` UUID (`WorldConfig.settingId`). */
  setting_id?: string
  /** Extra api book ids the world lists beyond its setting (`WorldConfig.extraBookIds`). */
  extra_book_ids?: readonly string[]
}

/**
 * The ONE listing source for every public book surface (store, home featured
 * tomes, live feed, detail-page gate): api's `/books` (optionally scoped to
 * the world's `setting_id`) plus the world's `extra_book_ids`, ∩ Atlas's
 * sellable catalog. Deny-by-default — until the catalog has loaded nothing is
 * listed, and a book with no Atlas deliverable never appears, whatever api
 * says about it (extra ids included).
 */
export function useStoreBooks(params: StoreBooksScope = {}) {
  // A world with no `pub_setting` of its own has no setting shelf: api's
  // `/books` is otherwise UNSCOPED and would hand it every other world's
  // sellable books (verified live on zweihander.games 2026-09-13). So no
  // setting ⇒ only the explicitly listed extra ids, if any.
  const scoped = !!params.setting_id
  const extraIds = useMemo(() => [...new Set(params.extra_book_ids ?? [])], [params.extra_book_ids])
  const listed = scoped || extraIds.length > 0
  // api's `/books` page size is the only paging knob it honours (`limit` is
  // dropped by its schema); a world's shelf is well under one page.
  const books = useBooks({ pageSize: 200, setting_id: params.setting_id }, { enabled: scoped })
  const extras = useQueries({
    queries: extraIds.map((id) => ({ queryKey: qk.book(id), queryFn: () => rpg.get<Book>(`/books/${id}`) })),
  })
  const catalog = useBookCatalog()
  const extraBooks = extras.map((q) => q.data).filter((b): b is Book => !!b)
  const extrasKey = extraBooks.map((b) => b.id).join(',')
  const data = useMemo(() => {
    if (!listed) return []
    const all = [...(scoped ? books.data?.data ?? [] : [])]
    const seen = new Set(all.map((b) => b.id))
    for (const b of extraBooks) if (!seen.has(b.id)) { seen.add(b.id); all.push(b) }
    return sellableBooks(all, catalog.byBookId)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- extraBooks is tracked by extrasKey
  }, [listed, scoped, books.data, extrasKey, catalog.byBookId])
  // An extra id that fails to load (deleted / renamed book) just drops out;
  // it never errors the whole shelf.
  const extrasLoading = extras.some((q) => q.isLoading)
  return {
    books: data,
    catalog: catalog.byBookId,
    isLoading: listed && ((scoped && books.isLoading) || extrasLoading || catalog.isLoading),
    isError: listed && ((scoped && books.isError) || catalog.isError),
    error: listed ? ((scoped ? books.error : null) ?? catalog.error) : null,
    refetch: () => {
      if (scoped) void books.refetch()
      for (const q of extras) void q.refetch()
      void catalog.refetch()
    },
  }
}

export function useBook(id: string | undefined) {
  return useQuery({ enabled: !!id, queryKey: qk.book(id!), queryFn: () => rpg.get<Book>(`/books/${id}`) })
}
export function useBookFull(id: string | undefined) {
  return useQuery({ enabled: !!id, queryKey: qk.bookFull(id!), queryFn: () => rpg.get<BookFull>(`/books/${id}/full`) })
}

// ── Book content (feature "5 Book ref" — the detail half) ──────────────────
/**
 * The book-content detail, fetched through the BFF (`GET /api/play/books/:id/
 * content`). The route is under `/api` so the shared `app` client carries the
 * Atlas Bearer and transparently refreshes it on a 401 (see client.ts). We map
 * the meaningful upstream states to a discriminant the card can render directly
 * rather than throwing, so loading a reference never error-boundaries the table.
 */
export type BookContent =
  | { state: 'ok'; title?: string; html?: string; body?: string; raw: Record<string, unknown> }
  | { state: 'not-owned' }
  | { state: 'content-down' }

/** Pull a display title + HTML/text body out of the (permissive) content payload. */
function shapeBookContent(raw: Record<string, unknown>): BookContent {
  const title = typeof raw.title === 'string' ? raw.title : undefined
  const html = typeof raw.html === 'string' ? raw.html : typeof raw.content_html === 'string' ? raw.content_html : undefined
  const body =
    typeof raw.body === 'string' ? raw.body : typeof raw.text === 'string' ? raw.text : typeof raw.content === 'string' ? raw.content : undefined
  return { state: 'ok', title, html, body, raw }
}

/**
 * Fetch a book's content for the reference detail view. `enabled` defaults to
 * true; pass `false` (e.g. while the detail is closed) to keep it dormant. The
 * BFF proxies play's ownership-gated content search, so the filters are the
 * content-search params `q`/`type`/`limit` (NOT section/page — play uses the
 * search contract). Never throws — a 403 resolves to `not-owned`, a 503 (or any
 * unreachable upstream) to `content-down`.
 */
export function useBookContent(
  bookId: string | undefined,
  { q, type, limit, enabled = true }: { q?: string; type?: string; limit?: number; enabled?: boolean } = {},
) {
  return useQuery<BookContent>({
    enabled: enabled && !!bookId,
    queryKey: ['book-content', bookId, { q, type, limit }],
    queryFn: async () => {
      try {
        const raw = await app.get<Record<string, unknown>>(`/play/books/${bookId}/content`, { q, type, limit })
        return shapeBookContent(raw ?? {})
      } catch (e) {
        if (e instanceof ApiError) {
          if (e.status === 403) return { state: 'not-owned' }
          if (e.status === 503) return { state: 'content-down' }
        }
        // Any other failure (502 from a dead upstream, network) → content-down.
        return { state: 'content-down' }
      }
    },
    placeholderData: keepPreviousData,
  })
}

// ── Encyclopedia facets: game systems + settings (from the active world) ────
// `FacetOption` + the per-world system/setting lists live in the world config;
// re-export the type so existing `@/lib/api/rpgApi` imports keep working.
export type { FacetOption }

/**
 * Game systems offered as an encyclopedia filter, for the active world. Driven by
 * `WorldConfig.gameSystems` (see `src/world.config.ts`). A function (not a const)
 * so it reads the registered config lazily — never at module load.
 */
export function getGameSystems(): FacetOption[] {
  return getActiveWorldConfig().gameSystems
}

/**
 * True when content tagged to `id` can actually be shown — i.e. no system is
 * selected, or the selected system is `available` in the active world. Selecting
 * a forthcoming system returns false so the encyclopedia shows nothing rather
 * than leaking unrelated content. Remove this gate once the API filters
 * `/entities` by `game_system` for every offered system.
 */
export function isLiveSystem(id?: string): boolean {
  if (!id) return true
  return getActiveWorldConfig().gameSystems.some((s) => s.id === id && s.available)
}

/**
 * Campaign settings offered as an encyclopedia filter, for the active world.
 * Driven by `WorldConfig.settings`. An `available` setting has live api tagging;
 * a forthcoming one yields an honest empty result rather than the untagged catalog.
 */
export function getSettings(): FacetOption[] {
  return getActiveWorldConfig().settings
}

/**
 * True when content tagged to setting `id` can be shown — i.e. no setting is
 * selected, or the selected setting is `available` in the active world.
 */
/** A setting facet that lists across every game system (see FacetOption.anySystem). */
export function settingAnySystem(id?: string): boolean {
  return !!id && getActiveWorldConfig().settings.some((s) => s.id === id && s.anySystem)
}

export function isLiveSetting(id?: string): boolean {
  if (!id) return true
  return getActiveWorldConfig().settings.some((s) => s.id === id && s.available)
}

/**
 * Canon layer / provenance — ORTHOGONAL to game system. Distinguishes content
 * that is *from* the SRD document itself from content that merely *uses* those
 * rules. See _docs/13-provenance-and-canon-layers.md.
 *  - `srd`      official open SRD-document content (e.g. the Aboleth)
 *  - `official` first-party Grymwyrd canon authored on a ruleset
 *  - `homebrew` community / player-canon contributions
 */
export const CANON_LAYERS: FacetOption[] = [
  { id: 'srd', label: 'SRD Canon' },
  { id: 'official', label: 'Official Canon' },
  { id: 'homebrew', label: 'Homebrew' },
]

// ── Encyclopedia entities (unified, returns rendered HTML) ─────────────────
export interface EntityQuery {
  type: EntityType
  q?: string
  page?: number
  pageSize?: number
  format?: EntityFormat
  /** Game-system (ruleset) filter (forward-compatible; ignored until the API supports it). */
  system?: string
  /** Setting filter (forward-compatible; ignored until the API supports it). */
  setting?: string
  /** Canon-layer / provenance filter (srd | official | homebrew). Forward-compatible. */
  canon?: string
  /**
   * Request the per-row provenance badge (`?provenance=true`) so each row carries
   * `provenance.setting_slugs` for accurate per-entity lock decisions inside a
   * mixed/unfiltered list (api PR #89 — batched server-side, no N+1). Off by
   * default; the lightweight autocomplete/global-search paths don't need it.
   */
  provenance?: boolean
  /**
   * Content locale for the api translation overlay (`?locale=`). Threaded through
   * to the `/system-entities` read (ZW). For the typed-5e `/entities` path it's a
   * forward-compatible pass-through (ignored by api until it overlays there).
   */
  locale?: string
}

/**
 * The system this world's encyclopedia reads by default — its first `available`
 * game system.
 *
 * WHY THIS REPLACED `published` + `realm`. Until 2026-07-25 both encyclopedia reads
 * sent `published: true` and `realm: <worldId>`, which resolve through
 * pub_book_entity → pub_book.is_public → pub_book_realm. Measured live, EVERY world
 * returned 0 for every entity type while the unfiltered count was 388 — all ten
 * encyclopedias were empty.
 *
 * The gate could never have worked: SRD content is not attached to any book WE
 * published, so asking "is this in a published book of ours?" has no affirmative
 * answer. A publication gate is right for our own authored content and wrong for
 * system canon.
 *
 * The model (Jared, 2026-07-25 — see brain `_platform/decisions/
 * encyclopedia-content-model.md`): system canon is OPEN, and our authored books are
 * entitlement-gated per book. So layer 1 filters positively by system instead. Layer 2
 * is a no-op today — api reports 0 rows at `canon=official` and `canon=homebrew`, so
 * there is nothing authored to gate yet; it needs per-book attribution from api first.
 */
function defaultSystemId(): string | undefined {
  return getActiveWorldConfig().gameSystems.find((s) => s.available)?.id
}

export function useEntities({ type, q, page = 1, pageSize = 24, format = 'name', system, setting, canon, provenance, locale }: EntityQuery) {
  // Unified path: `/entities/:type` now handles both typed-5e and system_entity-backed content.
  // The API filters by game_system parameter for both cases.
  const params = {
    q,
    page,
    pageSize,
    format,
    game_system: settingAnySystem(setting) ? undefined : system ?? defaultSystemId(),
    setting,
    canon,
    provenance: provenance || undefined,
    locale: locale || undefined,
  }
  const live = isLiveSystem(system) && isLiveSetting(setting)
  return useQuery({
    queryKey: qk.entities(type, params),
    queryFn: async () => {
      if (!live) return { type, format, page, pageSize, total: 0, data: [] } as EntityListResponse
      return rpg.get<EntityListResponse>(`/entities/${type}`, params)
    },
    placeholderData: keepPreviousData,
  })
}
/**
 * Fetch the FULL set of lightweight `format='name'` rows for a type, paging the
 * `/entities` endpoint until every row is loaded. The API has no server-side
 * prefix filter, so the A–Z index must filter + paginate over the whole type
 * client-side. `name` rows are tiny ({id, html}), so a couple of requests cover
 * the few hundred entities per type. Cached by (type + facets) so it's fetched
 * once. Disabled (and untouched) while searching or when a facet gate is empty.
 */
const ALL_NAMES_PAGE_SIZE = 200
const ALL_NAMES_MAX_PAGES = 12 // ≤ 2400 rows — ample headroom over ~400 per type
export function useAllEntityNames(
  type: EntityType,
  { system, setting, canon, locale }: { system?: string; setting?: string; canon?: string; locale?: string } = {},
  enabled = true,
) {
  const live = isLiveSystem(system) && isLiveSetting(setting)
  return useQuery({
    enabled: enabled && live,
    queryKey: ['entity-names', type, { system, setting, canon, locale }],
    queryFn: async () => {
      const rows: EntityRef[] = []
      let total = Infinity
      for (let page = 1; page <= ALL_NAMES_MAX_PAGES && rows.length < total; page++) {
        // Unified path: `/entities/:type` now handles both typed-5e and system_entity-backed content.
        // `provenance=true` badges each name row with its setting/book/genre tags for accurate
        // per-entity entitlement gating even inside an unfiltered/mixed list (batched server-side).
        const res = await rpg.get<EntityListResponse>(`/entities/${type}`, {
          format: 'name' as EntityFormat,
          game_system: settingAnySystem(setting) ? undefined : system ?? defaultSystemId(),
          setting,
          canon,
          provenance: true,
          page,
          pageSize: ALL_NAMES_PAGE_SIZE,
          locale: locale || undefined,
        })
        total = res.total
        rows.push(...res.data)
        if (res.data.length === 0) break
      }
      return rows
    },
    staleTime: 5 * 60 * 1000,
    placeholderData: keepPreviousData,
  })
}

export function useEntity(
  type: EntityType | undefined,
  id: string | undefined,
  format: EntityFormat = 'stat-block',
  opts: { system?: string; locale?: string } = {},
) {
  return useQuery({
    enabled: !!type && !!id,
    queryKey: qk.entity(type ?? '', id ?? '', format),
    queryFn: async () => {
      // Unified path: `/entities/:type/:id` now handles both SRD and system_entity ids (opaque strings).
      // `provenance=true` returns the entity's setting/book/genre tags for accurate per-entity
      // entitlement gating (isEntityLocked). The detail page consumes this for lock decisions.
      return rpg.get<EntityRef>(`/entities/${type}/${id}`, {
        format,
        provenance: true,
        locale: opts.locale || undefined,
      })
    },
  })
}

/**
 * Fetch a single entity by id and type using the unified path (`/entities/:type/:id`).
 * Handy when the caller already knows the entity type and id directly.
 */
export function useSystemEntity(id: string | undefined, type: EntityType, locale?: string) {
  return useQuery({
    enabled: !!id,
    queryKey: qk.entity(type, id ?? '', 'stat-block'),
    queryFn: async () => {
      return rpg.get<EntityRef>(`/entities/${type}/${id}`, {
        format: 'stat-block',
        provenance: true,
        locale: locale || undefined,
      })
    },
  })
}

// ── Entity assets (artwork / portraits) ────────────────────────────────────
export interface EntityAsset {
  id?: string | number
  url?: string
  thumbnail_url?: string
  /** e.g. "portrait", "scene", "map". */
  kind?: string
  caption?: string | null
  alt?: string | null
  [k: string]: unknown
}
interface EntityAssetsResponse {
  total?: number
  items?: EntityAsset[]
  data?: EntityAsset[]
}

/** Flatten the assets envelope ({items} | {data} | bare array) into a list. */
export function assetList(raw: unknown): EntityAsset[] {
  if (Array.isArray(raw)) return raw as EntityAsset[]
  if (raw && typeof raw === 'object') {
    const o = raw as EntityAssetsResponse
    if (Array.isArray(o.items)) return o.items
    if (Array.isArray(o.data)) return o.data
  }
  return []
}


/** The lead image for an entity: a portrait if present, else the first asset with a URL. */
export function primaryAsset(assets: EntityAsset[]): EntityAsset | undefined {
  return assets.find((a) => a.kind === 'portrait' && (a.url || a.thumbnail_url)) ?? assets.find((a) => a.url || a.thumbnail_url)
}

/**
 * Artwork bound to an entity. Tolerant of an absent/erroring assets endpoint
 * (resolves to an empty list) so cards and detail pages degrade to a placeholder
 * instead of failing. Cached for 5 minutes; shared by cards, the detail hero,
 * and the Gallery tab via one query key.
 */
export function useEntityAssets(type: EntityType | undefined, id: string | number | undefined, enabled = true) {
  return useQuery({
    enabled: enabled && !!type && id != null,
    queryKey: ['entity-assets', type, String(id)],
    queryFn: () =>
      rpg
        .get<EntityAssetsResponse>(`/entities/${type}/${id}/assets`, {
        })
        .catch(() => ({ items: [] as EntityAsset[] })),
    staleTime: 5 * 60 * 1000,
  })
}

// ── Encyclopedia articles (the consolidated entry) ─────────────────────────

/** One related entry on an article. `id` is absent for a place only the city knows (unlinked). */
export interface ArticleRelation {
  rel: string
  type?: string
  id?: number | string | null
  label: string
  note?: string
}
export interface ArticleSection {
  heading: string
  body: string
}
/** The consolidated encyclopedia article (api `GET /entities/:type/:id/encyclopedia`). */
export interface EncyclopediaArticle {
  key: string
  setting: string
  era?: { label: string }
  title: string
  summary: string
  lore: ArticleSection[]
  /** At-a-glance facts: strings, string lists, or lists of `{ name }` people. */
  glance: Record<string, string | string[] | { name: string }[]>
  relations: ArticleRelation[]
  cityLink?: { path: string; place: string; label: string }
}
/**
 * The "Specific book" view of the same entity. `sealed` means the book's own text is not shown publicly (it
 * carries the book's reveals); `books` names where to read it.
 */
export interface EncyclopediaBookView {
  sealed: boolean
  text: string | null
  books: { id: string; title: string }[]
}
export interface EncyclopediaResponse {
  type: string
  id: number | string
  article: EncyclopediaArticle
  book: EncyclopediaBookView
}

/**
 * The entity's consolidated article, or `null` when it has none (404): callers then keep the stat-block render.
 * Shared by the Lore tab and the detail header (one query key).
 */
export function useEncyclopediaArticle(type: EntityType | undefined, id: string | number | undefined) {
  return useQuery({
    enabled: !!type && id != null,
    queryKey: ['entity-encyclopedia', type, String(id)],
    queryFn: () =>
      rpg.get<EncyclopediaResponse>(`/entities/${type}/${id}/encyclopedia`).catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 404) return null
        throw err
      }),
    staleTime: 5 * 60 * 1000,
    retry: (n, err) => !(err instanceof ApiError && err.status === 404) && n < 2,
  })
}

interface EntityCardV1Response {
  card?: { image?: string | null }
}

/**
 * The `card.v1` lead image for an entity. Some types (character-class, species)
 * bind their art on this field rather than the `/assets` endpoint, so it's the
 * fallback art source for {@link EntityImage}. Tolerant of errors (resolves to
 * null) and cached 5 minutes; pass `enabled=false` to skip the fetch when an
 * `/assets` portrait already won.
 */
export function useEntityCardImage(type: EntityType | undefined, id: string | number | undefined, enabled = true) {
  return useQuery({
    enabled: enabled && !!type && id != null,
    queryKey: ['entity-card-image', type, String(id)],
    queryFn: () =>
      rpg
        .get<EntityCardV1Response>(`/entities/${type}/${id}`, {
          format: 'card.v1',
        })
        .then((r) => r.card?.image ?? null)
        .catch(() => null),
    staleTime: 5 * 60 * 1000,
  })
}

// ── Structured type-specific catalogs (full rows for filtering/sorting) ────
export function useMonsters(params: { page?: number; limit?: number } = {}) {
  return useQuery({
    queryKey: qk.monsters(params),
    queryFn: () => rpg.get<Paginated<Monster>>('/monsters', params),
    placeholderData: keepPreviousData,
  })
}
export function useSpells(params: { page?: number; limit?: number } = {}) {
  return useQuery({
    queryKey: qk.spells(params),
    queryFn: () => rpg.get<Paginated<Spell>>('/spells', params),
    placeholderData: keepPreviousData,
  })
}

// ── 3D asset kits (hex-tile map builder) ───────────────────────────────────
/**
 * A named collection of 3D models (e.g. the Pixen Intrigue hex-terrain-tile
 * kit) — `GET /3d-kits/:id`. Consumed by {@link HexBuilder} for its tile
 * palette; each `members[].asset` is a distinct GLB the builder can place.
 */
export function useKit3D(id: string | undefined) {
  return useQuery({
    enabled: !!id,
    queryKey: ['3d-kit', id],
    queryFn: () => rpg.get<Kit3D>(`/3d-kits/${id}`),
    staleTime: 10 * 60 * 1000,
  })
}

/**
 * A time-limited signed GET URL for a private, R2-hosted 3D model —
 * `GET /3d-models/:id/signed-url`. Assets are private, so every model load
 * (via `useGLTF`) must go through this first. Cached client-side well inside
 * the server's `expiresIn` window (default 3600s) so placing many instances of
 * the same tile type only fetches one signed URL, keeping `useGLTF`'s own
 * cache (keyed by URL string) from re-fetching per-instance. Disabled until an
 * `assetId` is known.
 */
export function useSignedModelUrl(assetId: string | undefined, expiresIn = 3600) {
  return useQuery({
    enabled: !!assetId,
    queryKey: ['3d-model-signed-url', assetId],
    queryFn: () => rpg.get<SignedModelUrl>(`/3d-models/${assetId}/signed-url`, { expiresIn }),
    staleTime: (expiresIn - 300) * 1000, // refresh a bit before the URL actually expires
  })
}

/**
 * Cross-type encyclopedia search — fans out across entity types in parallel.
 * Includes system-appropriate types: SRD types for 5e systems, ZW types for
 * Zweihänder Reforged. If `types` is provided, uses that instead of auto-detection.
 */
export function useGlobalEntitySearch(q: string, types?: EntityType[]) {
  // Auto-detect system-appropriate types if not provided explicitly
  const searchTypes = types ?? (() => {
    const config = getActiveWorldConfig()
    const defaultSystem = config.defaultSystem
    return getEntityTypes(defaultSystem).slice(0, 12)
  })()

  return useQuery({
    enabled: q.trim().length >= 2,
    queryKey: ['global-search', q, searchTypes],
    queryFn: async () => {
      const results = await Promise.all(
        searchTypes.map((type) =>
          rpg
            .get<EntityListResponse>(`/entities/${type}`, {
              q,
              pageSize: 5,
              format: 'name',
            })
            .then((r) => ({ type, items: r.data, total: r.total }))
            .catch(() => ({ type, items: [] as EntityRef[], total: 0 })),
        ),
      )
      return results.filter((r) => r.items.length > 0)
    },
  })
}
